import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { prefersReducedMotion } from '@theme/utilities';

const GRID_VIDEO_VISIBILITY_THRESHOLD = 0.5;

const GALLERY_PARENT_MORPH_SELECTORS = ['main', 'featured-product-information', 'quick-add-modal-content'];

/**
 * @typedef {HTMLElement & {
 *   loadContent: (focus?: boolean) => void,
 *   playMedia: () => void,
 *   pauseMedia: () => void,
 *   resetMedia: () => void,
 * }} DeferredMediaElement
 */

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {AbortController | null} */
  #videoAutoplayController = null;

  /** @type {AbortController | null} */
  #videoAutoplayModeController = null;

  /** @type {IntersectionObserver | null} */
  #videoObserver = null;

  /** @type {Map<Element, number>} */
  #visibleVideos = new Map();

  /** @type {AbortController | null} */
  #zoomDialogController = null;

  /** @type {AbortController | null} */
  #variantUpdateController = null;

  /** @type {number} */
  #videoAutoplaySetupGeneration = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#variantUpdateController?.abort();
    this.#variantUpdateController = new AbortController();

    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal: this.#variantUpdateController.signal,
    });

    this.#bindZoomDialogListener();
    this.#setupVideoAutoplay();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindZoomDialogListener();
  }

  #bindZoomDialogListener() {
    this.#zoomDialogController?.abort();
    this.#zoomDialogController = new AbortController();

    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal: this.#zoomDialogController.signal,
    });

    this.refs.zoomDialogComponent?.refs?.dialog?.addEventListener('close', this.#handleZoomDialogClose, {
      signal: this.#zoomDialogController.signal,
    });
  }

  #handleZoomDialogClose = () => {
    if (!this.#isGalleryVideoAutoplayEnabled()) return;

    this.#scheduleVideoAutoplayResume();
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#variantUpdateController?.abort();
    this.#variantUpdateController = null;
    this.#zoomDialogController?.abort();
    this.#zoomDialogController = null;
    this.#teardownVideoAutoplay();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    const mediaGallerySnapshot = event.detail.data.mediaGalleryHtml;

    if (!source && !mediaGallerySnapshot) return;

    const newMediaGallery = source?.querySelector('media-gallery') ?? null;
    const refreshSource = mediaGallerySnapshot ?? newMediaGallery;
    const isCombinedListingProductSwitch = Boolean(event.detail.data.newProduct);
    const galleryUpdatedByParentMorph = GALLERY_PARENT_MORPH_SELECTORS.includes(
      event.detail.data.morphElementSelector ?? ''
    );

    if (!refreshSource) return;

    this.#pauseAllGalleryVideos();
    this.#resetAllDeferredMedia();

    if (newMediaGallery && !(isCombinedListingProductSwitch && galleryUpdatedByParentMorph)) {
      morph(this, newMediaGallery, MORPH_OPTIONS);
    }

    this.#refreshDeferredMediaFromSource(refreshSource);
    this.#refreshZoomDialogFromSource(refreshSource);
    this.updatedCallback();
    this.#bindZoomDialogListener();
    this.#syncSlideshowAfterVariantUpdate();
    this.#teardownVideoAutoplay();
    this.#setupVideoAutoplay();
    this.#scheduleVideoAutoplayResume();
  };

  /**
   * @param {Element} deferredMedia
   */
  #sanitizeDeferredMediaElement(deferredMedia) {
    deferredMedia.removeAttribute('autoplay');
    deferredMedia.querySelectorAll(':scope > video, :scope > iframe[data-video-type]').forEach((element) => {
      element.remove();
    });
    deferredMedia.removeAttribute('data-media-loaded');
    deferredMedia.removeAttribute('data-user-paused');
    deferredMedia.removeAttribute('paused');
    deferredMedia.querySelector('.deferred-media__poster-button')?.classList.remove('deferred-media__playing');
  }

  #selectInitialSlideshowSlide() {
    const slideshow = this.refs.slideshow ?? this.querySelector('slideshow-component');
    if (!slideshow?.select || !slideshow.slides?.length) return false;

    const initialIndex = slideshow.initialSlideIndex ?? 0;
    const slideId = slideshow.refs?.slides?.[initialIndex]?.getAttribute('slide-id');

    if (slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
    } else {
      slideshow.select(initialIndex, undefined, { animate: false });
    }

    return slideshow.current === initialIndex;
  }

  /** @param {number} [attempt] */
  #syncSlideshowAfterVariantUpdate(attempt = 0) {
    if (attempt > 8) return;

    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      if (!this.#selectInitialSlideshowSlide()) {
        this.#syncSlideshowAfterVariantUpdate(attempt + 1);
      }
    });
  }

  /**
   * @param {ParentNode} sourceRoot
   */
  #refreshZoomDialogFromSource(sourceRoot) {
    const sourceDialog = sourceRoot.querySelector('zoom-dialog dialog');
    const currentDialog = this.querySelector('zoom-dialog dialog');

    if (!sourceDialog || !currentDialog) return;

    const sourceGallery = sourceDialog.querySelector('.dialog-zoomed-gallery');
    const currentGallery = currentDialog.querySelector('.dialog-zoomed-gallery');

    if (sourceGallery && currentGallery) {
      currentGallery.replaceWith(document.importNode(sourceGallery, true));
    }

    const sourceThumbnails = sourceDialog.querySelector('.dialog-thumbnails-list');
    const currentThumbnails = currentDialog.querySelector('.dialog-thumbnails-list');

    if (sourceThumbnails && currentThumbnails) {
      currentThumbnails.replaceWith(document.importNode(sourceThumbnails, true));
    }
  }

  /**
   * Morph does not update <template> contents, so loaded videos keep the previous product's media.
   * @param {ParentNode} sourceRoot
   */
  #refreshDeferredMediaFromSource(sourceRoot) {
    /** @type {Map<string, Element>} */
    const sourceDeferredByMediaId = new Map();

    sourceRoot.querySelectorAll('.product-media[data-media-id]').forEach((productMedia) => {
      const mediaId = productMedia.getAttribute('data-media-id');
      const deferredMedia = productMedia.querySelector('deferred-media');

      if (mediaId && deferredMedia) {
        sourceDeferredByMediaId.set(mediaId, deferredMedia);
      }
    });

    this.querySelectorAll('.product-media[data-media-id]').forEach((productMedia) => {
      const mediaId = productMedia.getAttribute('data-media-id');
      if (!mediaId) return;

      const currentDeferred = productMedia.querySelector('deferred-media');
      const sourceDeferred = sourceDeferredByMediaId.get(mediaId);

      if (typeof currentDeferred?.resetMedia === 'function') {
        currentDeferred.resetMedia();
      }

      if (!sourceDeferred) {
        if (currentDeferred) {
          this.#sanitizeDeferredMediaElement(currentDeferred);
        }

        return;
      }

      const importedDeferred = document.importNode(sourceDeferred, true);
      this.#sanitizeDeferredMediaElement(importedDeferred);

      if (currentDeferred) {
        currentDeferred.replaceWith(importedDeferred);
        return;
      }

      productMedia.appendChild(importedDeferred);
    });

    this.querySelectorAll('deferred-media').forEach((deferredMedia) => {
      this.#sanitizeDeferredMediaElement(deferredMedia);
    });
  }

  #resetAllDeferredMedia() {
    this.querySelectorAll('deferred-media').forEach((deferredMedia) => {
      if (typeof deferredMedia.resetMedia === 'function') {
        deferredMedia.resetMedia();
      }
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });

    if (!this.#isGalleryVideoAutoplayEnabled()) return;

    const zoomMediaItems = this.refs.zoomDialogComponent?.refs.media;
    zoomMediaItems?.forEach((media, index) => {
      if (index === event.detail.index) return;

      this.#pauseGalleryVideo(media);
    });

    const zoomMedia = zoomMediaItems?.[event.detail.index];
    if (!zoomMedia?.matches('.product-media-container--video, .product-media-container--external_video')) return;

    this.#pauseAllGalleryVideos();
    this.#playGalleryVideo(zoomMedia);
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.#pauseAllGalleryVideos();
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #isGalleryVideoAutoplayEnabled() {
    return this.dataset.videoAutoplay === 'true' && !prefersReducedMotion();
  }

  #setupVideoAutoplay() {
    if (this.dataset.videoAutoplay !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const generation = ++this.#videoAutoplaySetupGeneration;

    customElements.whenDefined('deferred-media').then(() => {
      if (generation !== this.#videoAutoplaySetupGeneration || !this.isConnected) return;

      requestAnimationFrame(() => {
        if (generation !== this.#videoAutoplaySetupGeneration || !this.isConnected) return;

        this.#videoAutoplayController = new AbortController();
        const { signal } = this.#videoAutoplayController;

        document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
        window.matchMedia('(min-width: 750px)').addEventListener('change', this.#handleVideoAutoplayBreakpoint, {
          signal,
        });

        this.#initVideoAutoplayMode();
      });
    });
  }

  #teardownVideoAutoplay() {
    this.#videoAutoplaySetupGeneration++;
    this.#videoAutoplayModeController?.abort();
    this.#videoAutoplayModeController = null;
    this.#videoAutoplayController?.abort();
    this.#videoAutoplayController = null;
    this.#videoObserver?.disconnect();
    this.#videoObserver = null;
    this.#visibleVideos.clear();
  }

  #useGridVideoAutoplay() {
    return this.presentation === 'grid' && window.matchMedia('(min-width: 750px)').matches;
  }

  #handleVideoAutoplayBreakpoint = () => {
    this.#pauseAllGalleryVideos();
    this.#initVideoAutoplayMode();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#pauseAllGalleryVideos();
      return;
    }

    if (this.#useGridVideoAutoplay()) {
      this.#resumeVideoAutoplay();
      return;
    }

    const slideshow = this.slideshow;
    const slide = slideshow?.slides?.[slideshow.current ?? 0];
    if (slide) this.#setActiveSlideVideo(slide);
  };

  #initVideoAutoplayMode() {
    const generation = this.#videoAutoplaySetupGeneration;

    this.#videoAutoplayModeController?.abort();
    this.#videoAutoplayModeController = new AbortController();
    const { signal } = this.#videoAutoplayModeController;

    this.#videoObserver?.disconnect();
    this.#videoObserver = null;
    this.#visibleVideos.clear();

    if (this.#useGridVideoAutoplay()) {
      this.#setupGridVideoAutoplay(signal);
    } else {
      this.#setupSlideshowVideoAutoplay(signal);
    }

    requestAnimationFrame(() => {
      if (generation !== this.#videoAutoplaySetupGeneration || !this.isConnected) return;

      this.#resumeVideoAutoplay();
    });
  }

  /** @param {number} [attempt] */
  #scheduleVideoAutoplayResume(attempt = 0) {
    if (!this.#isGalleryVideoAutoplayEnabled() || attempt > 8) return;

    const generation = this.#videoAutoplaySetupGeneration;

    requestAnimationFrame(() => {
      if (generation !== this.#videoAutoplaySetupGeneration || !this.isConnected) return;

      if (!this.#resumeVideoAutoplay()) {
        this.#scheduleVideoAutoplayResume(attempt + 1);
      }
    });
  }

  /** @param {Element} element */
  #isVisibleInViewport(element) {
    const rect = element.getBoundingClientRect();

    return rect.width > 0 && rect.height > 0;
  }

  /** @param {Element} element */
  #getVisibleRatio(element) {
    if (!this.#isVisibleInViewport(element)) return 0;

    const rect = element.getBoundingClientRect();
    const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);

    if (visibleHeight <= 0 || rect.height <= 0) return 0;

    return visibleHeight / rect.height;
  }

  /** @returns {boolean} */
  #resumeVideoAutoplay() {
    if (this.dataset.videoAutoplay !== 'true') return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

    if (this.#useGridVideoAutoplay()) {
      const videos = this.querySelectorAll(
        '.media-gallery__grid .product-media-container--video, .media-gallery__grid .product-media-container--external_video'
      );

      /** @type {Element | null} */
      let bestContainer = null;
      let bestRatio = 0;

      videos.forEach((container) => {
        const ratio = this.#getVisibleRatio(container);

        if (ratio >= GRID_VIDEO_VISIBILITY_THRESHOLD && ratio > bestRatio) {
          bestRatio = ratio;
          bestContainer = container;
        }
      });

      if (!bestContainer) return false;

      this.#pauseAllGalleryVideosExcept(bestContainer);
      return this.#playGalleryVideo(bestContainer);
    }

    const slideshow = this.querySelector('slideshow-component') ?? this.refs.slideshow;
    const slide = slideshow?.slides?.[slideshow.current ?? 0];

    if (!slide) return false;

    return this.#setActiveSlideVideo(slide);
  }

  /**
   * @param {AbortSignal} signal
   */
  #setupGridVideoAutoplay(signal) {
    const videos = this.querySelectorAll(
      '.media-gallery__grid .product-media-container--video, .media-gallery__grid .product-media-container--external_video'
    );

    if (!videos.length) return;

    this.#videoObserver = new IntersectionObserver(this.#handleGridVideoIntersection, {
      threshold: [0, GRID_VIDEO_VISIBILITY_THRESHOLD, 1],
    });

    videos.forEach((video) => {
      this.#videoObserver?.observe(video);
    });
  }

  /**
   * @param {AbortSignal} signal
   */
  #setupSlideshowVideoAutoplay(signal) {
    const slideshow = this.querySelector('slideshow-component') ?? this.refs.slideshow;
    if (!slideshow) return;

    slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowVideoSelect, { signal });

    requestAnimationFrame(() => {
      const slide = slideshow.slides?.[slideshow.current ?? 0];
      if (slide) this.#setActiveSlideVideo(slide);
    });
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleGridVideoIntersection = (entries) => {
    for (const entry of entries) {
      const container = entry.target;

      if (entry.isIntersecting && entry.intersectionRatio >= GRID_VIDEO_VISIBILITY_THRESHOLD) {
        this.#visibleVideos.set(container, entry.intersectionRatio);
      } else {
        this.#visibleVideos.delete(container);
        this.#pauseGalleryVideo(container);
      }
    }

    let bestContainer = null;
    let bestRatio = 0;

    for (const [container, ratio] of this.#visibleVideos) {
      if (ratio > bestRatio) {
        bestRatio = ratio;
        bestContainer = container;
      }
    }

    if (!bestContainer) return;

    this.#pauseAllGalleryVideosExcept(bestContainer);
    this.#playGalleryVideo(bestContainer);
  };

  /** @param {SlideshowSelectEvent} event */
  #handleSlideshowVideoSelect = (event) => {
    const slideshow = this.slideshow;
    const previousSlide = slideshow?.slides?.[event.detail.previousIndex];

    if (previousSlide) {
      this.#pauseGalleryVideo(previousSlide);
    }

    if (event.detail.slide) {
      this.#setActiveSlideVideo(event.detail.slide);
    }
  };

  /**
   * @param {Element} slide
   * @returns {boolean}
   */
  #setActiveSlideVideo(slide) {
    this.#pauseAllGalleryVideosExcept(slide);

    if (!slide.matches('.product-media-container--video, .product-media-container--external_video')) {
      return true;
    }

    return this.#playGalleryVideo(slide);
  }

  /** @param {Element} container */
  #playGalleryVideo(container) {
    const deferredMedia = /** @type {DeferredMediaElement | null} */ (container.querySelector('deferred-media'));
    if (!deferredMedia || deferredMedia.hasAttribute('data-user-paused')) return false;

    customElements.upgrade(deferredMedia);

    if (typeof deferredMedia.loadContent !== 'function' || typeof deferredMedia.playMedia !== 'function') {
      return false;
    }

    if (!deferredMedia.getAttribute('data-media-loaded')) {
      deferredMedia.loadContent(false);
    }

    deferredMedia.playMedia();

    return deferredMedia.hasAttribute('data-media-loaded');
  }

  /** @param {Element} container */
  #pauseGalleryVideo(container) {
    container.querySelector('deferred-media')?.pauseMedia?.();
  }

  #getGalleryVideoContainers() {
    return this.querySelectorAll(
      '.media-gallery__grid .product-media-container--video, .media-gallery__grid .product-media-container--external_video, slideshow-slide.product-media-container--video, slideshow-slide.product-media-container--external_video'
    );
  }

  #pauseAllGalleryVideos() {
    this.#getGalleryVideoContainers().forEach((container) => {
      this.#pauseGalleryVideo(container);
    });
  }

  /** @param {Element} exceptContainer */
  #pauseAllGalleryVideosExcept(exceptContainer) {
    this.#getGalleryVideoContainers().forEach((container) => {
      if (container === exceptContainer) return;

      this.#pauseGalleryVideo(container);
    });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
